import React, { useState } from 'react';
import { useAvailableBackups, AvailableBackup } from '../../hooks/useDeltaCompare';
import { formatDistanceToNow } from 'date-fns';
import { formatEastern } from '@/utils/dateFormatter';

interface CompareWizardProps {
  lakehouseId: string;
  lakehouseName: string;
  onCompare: (backupRunId: string, options: { tableFilter?: string[] }) => void;
  isComparing?: boolean;
}

export const CompareWizard: React.FC<CompareWizardProps> = ({
  lakehouseId,
  lakehouseName,
  onCompare,
  isComparing = false,
}) => {
  const [selectedBackup, setSelectedBackup] = useState<string | null>(null);
  const [tableFilter, setTableFilter] = useState('');

  const { data: backups, isLoading: isLoadingBackups } = useAvailableBackups(lakehouseId);

  const handleCompare = () => {
    if (!selectedBackup) {
      return;
    }

    const tables = tableFilter
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    onCompare(selectedBackup, {
      tableFilter: tables.length > 0 ? tables : undefined,
    });
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className='bg-[var(--color-bg-card)] shadow rounded-lg p-6'>
      <h3 className='text-lg font-medium text-[var(--color-text-primary)] mb-4'>Compare Backup</h3>

      <div className='space-y-4'>
        {/* Lakehouse Info */}
        <div className='bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4'>
          <div className='flex items-center'>
            <svg
              className='h-5 w-5 text-blue-500'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4'
              />
            </svg>
            <div className='ml-3'>
              <h4 className='text-sm font-medium text-blue-900 dark:text-blue-300'>Selected Lakehouse</h4>
              <p className='text-sm text-blue-700 dark:text-blue-400'>{lakehouseName}</p>
            </div>
          </div>
        </div>

        {/* Backup Selection */}
        <div>
          <label htmlFor='backup-select' className='block text-sm font-medium text-[var(--color-text-secondary)] mb-2'>
            Select Backup to Compare
          </label>
          {isLoadingBackups ? (
            <div className='flex items-center justify-center py-8'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500'></div>
            </div>
          ) : backups && backups.length > 0 ? (
            <select
              id='backup-select'
              value={selectedBackup || ''}
              onChange={(e) => setSelectedBackup(e.target.value)}
              className='block w-full border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded-md shadow-sm focus:ring-[var(--color-accent)] focus:border-[var(--color-accent)] sm:text-sm'
            >
              <option value=''>-- Select a backup --</option>
              {backups.map((backup) => (
                <option key={backup.id} value={backup.id}>
                  {formatEastern(backup.completed_at)} - {backup.backup_type} (
                  {backup.tables_backed_up} tables, {formatBytes(backup.total_size_bytes)})
                </option>
              ))}
            </select>
          ) : (
            <div className='text-sm text-[var(--color-text-secondary)] py-4 text-center'>
              No completed backups available for comparison
            </div>
          )}
        </div>

        {/* Selected Backup Details */}
        {selectedBackup && backups && (
          <div className='bg-[var(--color-bg-subtle)] border border-[var(--color-border)] rounded-md p-4'>
            {(() => {
              const backup = backups.find((b) => b.id === selectedBackup);
              if (!backup) return null;
              return (
                <div className='grid grid-cols-2 gap-4 text-sm'>
                  <div>
                    <span className='font-medium text-[var(--color-text-secondary)]'>Backup Date:</span>
                    <p className='text-[var(--color-text-primary)]'>
                      {formatDistanceToNow(new Date(backup.completed_at), { addSuffix: true })}
                    </p>
                  </div>
                  <div>
                    <span className='font-medium text-[var(--color-text-secondary)]'>Type:</span>
                    <p className='text-[var(--color-text-primary)]'>{backup.backup_type}</p>
                  </div>
                  <div>
                    <span className='font-medium text-[var(--color-text-secondary)]'>Tables:</span>
                    <p className='text-[var(--color-text-primary)]'>{backup.tables_backed_up}</p>
                  </div>
                  <div>
                    <span className='font-medium text-[var(--color-text-secondary)]'>Size:</span>
                    <p className='text-[var(--color-text-primary)]'>{formatBytes(backup.total_size_bytes)}</p>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Options */}
        <div className='space-y-3'>
          <h4 className='text-sm font-medium text-[var(--color-text-secondary)]'>Comparison Options</h4>

          <div>
            <label htmlFor='table-filter' className='block text-sm font-medium text-[var(--color-text-secondary)] mb-1'>
              Filter Tables (Optional)
            </label>
            <input
              type='text'
              id='table-filter'
              value={tableFilter}
              onChange={(e) => setTableFilter(e.target.value)}
              placeholder='e.g., sales, customers, products (comma-separated)'
              className='block w-full border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded-md shadow-sm focus:ring-[var(--color-accent)] focus:border-[var(--color-accent)] sm:text-sm'
            />
            <p className='mt-1 text-xs text-[var(--color-text-secondary)]'>Leave empty to compare all tables</p>
          </div>
        </div>

        {/* Compare Button */}
        <div className='pt-4'>
          <button
            onClick={handleCompare}
            disabled={!selectedBackup || isComparing}
            className='w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[var(--color-accent)] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--color-accent)] disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {isComparing ? (
              <>
                <svg
                  className='animate-spin -ml-1 mr-3 h-5 w-5 text-white'
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  ></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  ></path>
                </svg>
                Comparing...
              </>
            ) : (
              <>
                <svg
                  className='-ml-1 mr-2 h-5 w-5'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01'
                  />
                </svg>
                Run Comparison
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
